import * as React from 'react'
import { Formik, Field, FieldProps } from 'formik'

import * as service from '@owl-nest/services'
import * as plume from '@ulule/owl-kit-components/next'
import { SignupUser } from '@owl-nest/auth'
import * as env from '@owl-nest/config'
import { t, tc } from '@owl-nest/localize'
import { ua } from '@owl-nest/utils'

import { useAuthFailureMessage } from './useAuthFailureMessage'
import { useNewPasswordValidator } from './useNewPasswordValidator'
import { memorizeThirdPartyProvider } from './thirdPartyProvider'
import { validateEmail, apiErrorGetter, validateRequired } from './validators'

import * as S from './styles'

type Form = SignupUser & { terms: boolean }

type NewUserProps = {
  auth: service.user.UseAuthNew
  context?: string
  showHint?: boolean
}

export function NewUser({ auth, context, showHint = true }: NewUserProps): React.ReactElement<NewUserProps> {
  useAuthFailureMessage(auth)

  const [feedback, validateNewPassword] = useNewPasswordValidator()
  const getApiErrors = apiErrorGetter<Form>(auth.failure)
  const isWebView = ua.isWebview(window.navigator.userAgent)

  return (
    <>
      <Formik<Form>
        initialValues={{ ...(context && { context }), email: auth.identifier.value, password: '', terms: false }}
        onSubmit={auth.signup}
        validateOnBlur={false}
        validateOnChange={false}
      >
        {({ handleSubmit, values, validateField }) => {
          const apiError = getApiErrors({
            email: {
              ValueError: t('Enter a valid email address'),
              // FIXME: maybe API should use a better classification than InvalidValueError for bounce email
              InvalidValueError: t("This email is invalid and doesn't seem to be reachable"),
              AlreadyExistsError: tc('This email is already taken. Is it you ? [link: Log in]', {
                link: <plume.Link kind="primary" onClick={() => auth.toExisting(values.email, 'ulule')} />,
              }),
            },
            password: {
              UnusablePasswordError: tc('This email is already taken. Is it you ? [link: Log in]', {
                link: <plume.Link kind="primary" onClick={() => auth.toExisting(values.email, 'facebook')} />,
              }),
            },
          })
          return (
            <S.NewUserForm noValidate onSubmit={handleSubmit}>
              {showHint && (
                <plume.styles.copy.M.light>
                  {t('To continue, fill in your email or choose to log in with Facebook or Google.')}
                </plume.styles.copy.M.light>
              )}
              <Field name="email" validate={validateEmail}>
                {({ field, meta }: FieldProps<string>) => {
                  React.useEffect(() => {
                    if (field.value !== '') {
                      validateField(field.name)
                    }
                  }, [field.value])
                  return (
                    <plume.TextField
                      label={t('E-mail address')}
                      type="email"
                      name={field.name}
                      value={field.value}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      error={apiError('password') || apiError('email') || meta.error}
                    />
                  )
                }}
              </Field>{' '}
              <Field
                name="password"
                validate={(value: any) => {
                  if (!value) {
                    return t('This field is required')
                  } else {
                    return validateNewPassword(value)
                  }
                }}
              >
                {({ field, meta }: FieldProps<string>) => {
                  React.useEffect(() => {
                    if (field.value !== '') {
                      validateField(field.name)
                    }
                  }, [field.value])
                  return (
                    <plume.PasswordField
                      autoFocus
                      label={t('Create a password')}
                      name={field.name}
                      value={field.value}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      feedback={field.value ? feedback : undefined}
                      error={meta.touched && (meta.error === 'error' ? true : meta.error)}
                    />
                  )
                }}
              </Field>
              <Field name="terms" validate={validateRequired}>
                {({ field, meta }: FieldProps<boolean>) => {
                  return (
                    <plume.CheckboxField
                      label={tc(
                        'I declare that I am over 18 years old and have read, understood and accepted the [link: Terms of Use]',
                        {
                          link: <plume.Link href={env.TERMS_OF_USE_URL} target="_blank" rel="noopener" />,
                        },
                      )}
                      name={field.name}
                      checked={field.value}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      error={meta.touched ? meta.error : undefined}
                    />
                  )
                }}
              </Field>
              <plume.RoundButton fitToParent type="submit">
                {t('Continue')}
              </plume.RoundButton>
              <S.ButtonsSeparator>
                <S.HorizontalRuler />
                <S.OrLabel>{t('Or')}</S.OrLabel>
              </S.ButtonsSeparator>
              <plume.SocialButton
                platform="facebook"
                style="round"
                type="button"
                onClick={() => {
                  memorizeThirdPartyProvider('facebook')
                  window.location.href = `${env.FACEBOOK_LOGIN_URL}?next=${window.location.href}`
                }}
              >
                {t('Log in with Facebook')}
              </plume.SocialButton>
              {!isWebView && (
                <>
                  <plume.SocialButton
                    platform="google"
                    style="round"
                    type="button"
                    onClick={() => {
                      memorizeThirdPartyProvider('google')
                      window.location.href = `${env.GOOGLE_LOGIN_URL}?next=${window.location.href}`
                    }}
                  >
                    {t('Log in with Google')}
                  </plume.SocialButton>
                </>
              )}
              <S.ResetButton $adjustForHint={showHint} onClick={() => auth.forget()} type="button">
                {t('Reinitialize')}
              </S.ResetButton>
            </S.NewUserForm>
          )
        }}
      </Formik>
    </>
  )
}
